import React from 'react'
import dayjs from 'dayjs'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { AccountHeader } from '@mobi/component-library/Areas/Account'
import { Button } from '@mobi/component-library/Base/Buttons'
import { StepGroup } from '@mobi/component-library/Base/StepGroup'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import { Input, Checkbox } from '@mobi/component-library/Selection&Input/Inputs'
import { NameInput } from '@mobi/account/Common'
import { isValidName, getFormattedDateOfBirth, validateDateOfBirth, routes } from '../Utils'
import {
  getSignUpState,
  setHasUserStartedSignup,
  setSignupApplication,
  setSignupVerification,
  setUserKnownByOtherNames,
} from '../Store'
import { SignupError, BoldStyled, FooterStyled, FormSection, PersonalDetailsDisclaimer } from './'
import { signupTracker } from '@mobi/account/Common/Analytics'
import { SignUpPageStyled } from './SignUpPage.styled'
import { useApplicationCheck } from '../Hooks'

export const PersonalDetails = () => {
  const navigate = useNavigate()
  const [showErrorPopup, setShowErrorPopup] = React.useState(false)
  const state = useSelector(getSignUpState)
  const application = state.signupApplication
  const verification = state.signupVerification
  const dispatch = useDispatch()

  const { mutate: checkApplication, isPending: isCheckPending } = useApplicationCheck({
    onSuccess: data => {
      dispatch(
        setSignupVerification({
          ...verification,
          duplicateExists: data.duplicateExists,
          isDuplicateClosed: (data.accountStatus?.toLowerCase() ?? '') === 'closed',
        })
      )
      if (data.duplicateExists) {
        navigate(routes.signUpUnsuccessful)
        return
      }
      signupTracker({
        interaction_type: 'Name & DOB',
        signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
        alias: state.isUserKnownByOtherNames ? true : false,
      })
      navigate(routes.locationDetails)
    },
    onError: () => setShowErrorPopup(true),
  })

  const handleNext = () => {
    checkApplication({
      lastName: application.surname,
      birthdate: dayjs(application.birthdate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
    })
  }

  const isValidKnownByOtherNames =
    !state.isUserKnownByOtherNames ||
    isValidName(application.firstNameAlias) ||
    isValidName(application.lastNameAlias)

  const [validationMessages, setValidationMessages] = React.useState<Record<string, string>>({})

  const isOver18 = !!application.birthdate && !validationMessages.dateOfBirth
  const hasValidEntries = Object.values(validationMessages).every(error => error === '')
  const isComplete =
    hasValidEntries &&
    application.birthdate.length > 0 &&
    isValidName(application.firstName) &&
    isValidName(application.middleName, true) &&
    isValidName(application.surname) &&
    isValidKnownByOtherNames

  const resetFieldValidation = (field: keyof typeof validationMessages) =>
    setValidationMessages(prev => ({
      ...prev,
      [field]: '',
    }))

  const validateIsKnownByOtherNames = () => {
    setValidationMessages(prev => ({
      ...prev,
      isKnownByOtherNames:
        state.isUserKnownByOtherNames &&
        !isValidName(application.firstNameAlias ?? '') &&
        !isValidName(application.lastNameAlias ?? '')
          ? 'Please enter details or untick the box.'
          : '',
    }))
  }

  return (
    <>
      <SignUpPageStyled>
        <StepGroup currentStep={0} totalSteps={5} />
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            color='secondary_grey'
            onClick={() => {
              signupTracker({
                interaction_type: 'Already Have Account',
                signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
                signUpStarted: state.hasUserStartedSignup,
              })

              navigate('/account')
            }}
            type='button'
          >
            <span>
              Already have a <BoldStyled>TABtouch account?</BoldStyled>
            </span>
          </Button>
        </div>
        <AccountHeader
          alignItems='start'
          title="Let's get you started!"
          description={
            <InfoBox color='blue' size='sm' hideIcon={true}>
              Enter your full name and DOB as it appears on your ID documents (including middle name
              if applicable)
            </InfoBox>
          }
          style={{ marginBottom: 0 }}
        />

        <FormSection>
          <Input
            label='Date of Birth'
            name='dateOfBirth'
            placeholder='DD/MM/YYYY'
            required
            maxLength={10}
            value={application.birthdate}
            onChange={e =>
              dispatch(
                setSignupApplication({
                  ...application,
                  birthdate: getFormattedDateOfBirth(
                    e.target.value,
                    application.birthdate,
                    e.target.selectionStart
                  ),
                })
              )
            }
            onFocus={() => {
              resetFieldValidation('dateOfBirth')
              if (!state.hasUserStartedSignup) {
                signupTracker({
                  interaction_type: 'Sign Up Started',
                  signUpFlow: state.isVerificationAccount ? 'ID Verification' : 'Standard',
                })
                dispatch(setHasUserStartedSignup(true))
              }
            }}
            onBlur={e => {
              setValidationMessages(prev => ({
                ...prev,
                dateOfBirth: validateDateOfBirth(
                  getFormattedDateOfBirth(
                    e.target.value,
                    application.birthdate,
                    e.target.selectionStart
                  )
                ),
              }))
            }}
            isInvalid={!!validationMessages.dateOfBirth}
            helpText={
              validationMessages.dateOfBirth ||
              'Must match your ID documents · You must be 18 or over to wager in Australia'
            }
          />
          <NameInput
            firstName={application.firstName}
            middleName={application.middleName}
            lastName={application.surname}
            setFirstName={firstName =>
              dispatch(
                setSignupApplication({
                  ...application,
                  firstName,
                })
              )
            }
            setMiddleName={middleName =>
              dispatch(
                setSignupApplication({
                  ...application,
                  middleName,
                })
              )
            }
            setLastName={surname =>
              dispatch(
                setSignupApplication({
                  ...application,
                  surname,
                })
              )
            }
            isDisabled={!isOver18}
          />

          <Checkbox
            id='sign-up-known-by-other-names'
            name='isKnownByOtherNames'
            checked={state.isUserKnownByOtherNames}
            label='Are you known by any other names?'
            onChange={e => {
              dispatch(setUserKnownByOtherNames(e.target.checked))
              dispatch(
                setSignupApplication({
                  ...application,
                  firstNameAlias: '',
                  lastNameAlias: '',
                })
              )
              resetFieldValidation('isKnownByOtherNames')
            }}
            isDisabled={!isOver18}
          />

          {state.isUserKnownByOtherNames && (
            <>
              <Input
                type='text'
                label='First name (if applicable)'
                name='firstNameAlias'
                placeholder='Enter first name'
                value={application.firstNameAlias}
                onChange={e =>
                  dispatch(
                    setSignupApplication({
                      ...application,
                      firstNameAlias: e.target.value,
                    })
                  )
                }
                onBlur={validateIsKnownByOtherNames}
                isInvalid={!!validationMessages.isKnownByOtherNames}
              />
              <Input
                type='text'
                label='Last name (if applicable)'
                name='lastNameAlias'
                placeholder='Enter last name'
                value={application.lastNameAlias}
                onChange={e =>
                  dispatch(
                    setSignupApplication({
                      ...application,
                      lastNameAlias: e.target.value,
                    })
                  )
                }
                onBlur={validateIsKnownByOtherNames}
                helpText={validationMessages.isKnownByOtherNames}
                isInvalid={!!validationMessages.isKnownByOtherNames}
              />
            </>
          )}
        </FormSection>

        <FooterStyled>
          <>
            <Button
              type='button'
              isFullWidth
              isLoading={isCheckPending}
              disabled={!isComplete || !isOver18}
              onClick={handleNext}
            >
              Next
            </Button>
            <InfoBox color='grey' size='md' hideIcon={true}>
              <PersonalDetailsDisclaimer />
            </InfoBox>
          </>
        </FooterStyled>
      </SignUpPageStyled>
      <SignupError isOpen={showErrorPopup} onClose={() => setShowErrorPopup(false)} />
    </>
  )
}
